/**
 * /api/rules, from the browser (api/rules/index.ts). Callers hold the full-access role
 * and an 'admin' member row; for anyone else the page is not offered, and the API says
 * 403 regardless. Never sent while viewing as someone: the page is off then, and the
 * API refuses the header anyway.
 */
import { TOKEN_HEADER } from '@/data/client'
import { apiFetch } from '@/auth/apiFetch'
import type { AuditEntry, ImportResult, RuleSet, RuleTable } from './model'

export class RulesError extends Error {
  constructor(
    message: string,
    readonly status: number,
    /** One per refused row; `rule` names it ("HUMANA MLTC from 2026-01-01") when the API can. */
    readonly rowErrors: { row: number; rule?: string; error: string }[] = [],
  ) {
    super(message)
  }
}

/** How long before asking again when the functions host did not answer (tests set 0). */
export const rulesRetry = { delayMs: 1000 }

async function call<T>(token: string | null, method: string, path: string, body?: unknown, retried = false): Promise<T> {
  const headers: Record<string, string> = { Accept: 'application/json' }
  if (token) headers[TOKEN_HEADER] = token
  if (body !== undefined) headers['Content-Type'] = 'application/json'
  const res = await apiFetch(`/api/rules${path}`, {
    method,
    headers,
    body: body === undefined ? undefined : JSON.stringify(body),
  })
  const json = (await res.json().catch(() => null)) as {
    error?: string
    errors?: { row: number; rule?: string; error: string }[]
  } | null
  if (!res.ok) {
    // A 503 with no body is the functions host, not the API (cold start, restart): our code
    // never ran, so asking once more is safe. The API retries its own transient failures.
    if (res.status === 503 && json === null && !retried) {
      await new Promise((r) => setTimeout(r, rulesRetry.delayMs))
      return call<T>(token, method, path, body, true)
    }
    const message =
      res.status === 403
        ? 'Rules are limited to rule editors.'
        : res.status === 503
          ? // The API's own 503 names its cause and a log reference; without one, the
            // functions host itself did not answer (cold start, crash).
            (json?.error ?? 'Rules are not available right now: the server did not answer (503).')
          : (json?.error ?? `The request failed (${res.status}).`)
    throw new RulesError(message, res.status, json?.errors ?? [])
  }
  return json as T
}

export function rulesApi(token: string | null) {
  return {
    list: () => call<RuleSet>(token, 'GET', ''),
    audit: () => call<{ entries: AuditEntry[] }>(token, 'GET', '/audit').then((r) => r.entries),
    add: (table: RuleTable, fields: Record<string, unknown>) => call<unknown>(token, 'POST', `/${table}`, fields),
    update: (table: RuleTable, id: number, changes: Record<string, unknown>) =>
      call<unknown>(token, 'PATCH', `/${table}/${id}`, changes),
    /** A CSV ('import'), or a bulk edit made on the page ('page'): how the audit records it. */
    importRows: (table: RuleTable, rows: Record<string, string>[], source: 'import' | 'page' = 'import') =>
      call<ImportResult>(token, 'POST', '/import', { table, rows, source }),
  }
}
export type RulesApi = ReturnType<typeof rulesApi>
